import {Schema} from '../schema.js';
import {Model} from '../engine.js';
import {ListType, ObjectType, Property, TypeKind, TypeReference} from '../types.js';
import {PropertyValue} from './PropertyPathWalker.js';
import {ListSourcedValue, Option, SourcedValue} from '../option.js';

interface OneLevelWalker {
  next(): PropertyValue | undefined;
}

export class OneLevelListWalker implements OneLevelWalker {
  private schema: Schema;
  private propertyValue: PropertyValue;
  private type: ListType;
  private properties: string[] = [];
  private items: any[];
  private nextIndex = 0;
  private itemType: TypeReference;


  constructor(schema: Schema, propertyValue: PropertyValue, private stopProcessingCallback: (propertyValue:PropertyValue)=>boolean = null) {
    let fromSourcedValue = (propertyValue.property.opts.needSource && (propertyValue.value instanceof ListSourcedValue || propertyValue.value.value)) ? propertyValue.value.value : propertyValue.value;

    if (!(Array.isArray(fromSourcedValue))) {
      throw new Error(`Model '${JSON.stringify(fromSourcedValue)}' should be a list`)
    }
    this.propertyValue = propertyValue;
    this.schema = schema;
    let type = this.schema.getType(propertyValue.property.type);
    if (!(type instanceof ListType)) {
      throw new Error(`Type '${propertyValue.property.type}' should be an list type`);
    }
    this.type = type as ListType;
    this.itemType = this.type.elementType;
    this.items = fromSourcedValue.slice();
  }

  next(): PropertyValue | undefined {
    let currentItem = this.items[this.nextIndex];
    if (!currentItem) {
      return undefined;
    }
    const property: Property = new Property(this.nextIndex.toString(), this.itemType);
    this.nextIndex++;
    return { property: property, value: currentItem, parent: {parent: this.propertyValue.parent, property: this.propertyValue.property, value: this.items} };
  }
}

export class OneLevelModelWalker implements OneLevelWalker {
  private schema: Schema;
  private propertyValue: PropertyValue;
  private type: ObjectType;
  private properties: string[] = [];

  constructor(schema: Schema, propertyValue: PropertyValue, private stopProcessingCallback: (propertyValue:PropertyValue)=>boolean = null) {
    if (!(propertyValue.value instanceof Object)) {
      throw new Error(`Model '${propertyValue.value}' should be an object`)
    }
    this.propertyValue = propertyValue;
    this.schema = schema;

    let type = this.schema.getType(propertyValue.property.type);
    if (!(type instanceof ObjectType)) {
      throw new Error(`Type '${propertyValue.property.type}' should be an object type`);
    }
    this.type = type as ObjectType;
    // this iteration based on properties of model
    // for (let prop in this.model){
    //     this.properties.push(prop);
    // }
    // this iteration based on properties of type
    const fullProperties = schema.getFullObjectTypeProperties(type);
    for (const property of fullProperties) {
      this.properties.push(property.name);
    }
    this.properties.sort().reverse();
  }

  next(): PropertyValue | undefined {
    let currentPropertyName = this.properties.pop();
    if (!currentPropertyName) {
      return undefined;
    }
    let property = this.type.getProperty(currentPropertyName);
    if (!property) {
      throw new Error(`Property '${currentPropertyName}' not exist in type ${this.type.getTypeReference()}`);
    }
    const value = getPropertyValue(currentPropertyName, this.propertyValue.value);
    return { property: property, value: value, parent: this.propertyValue };
  }
}

const getPropertyValue = (name: string, value: any) => {
  if (value instanceof Option) {
    return value.model[name];
  } else if (value instanceof SourcedValue){
    return getPropertyValue(name, value.value);
  } else {
    return value[name];
  }
}

export class DeepModelWalker {
  private schema: Schema;
  private model: Model;
  private type: ObjectType;
  private walkers: OneLevelWalker[] = [];
  

  constructor(schema: Schema, model: Model, typeRef: TypeReference, private stopProcessingCallback: (propertyValue:PropertyValue)=>boolean = null) {
    const rootWalker: any = Array.isArray(model) ? OneLevelListWalker : OneLevelModelWalker;
    this.walkers.push(new rootWalker(schema, { parent: undefined, property: new Property('$', typeRef), value: model }, this.stopProcessingCallback));
    this.model = model;
    this.schema = schema;
    let type = this.schema.getType(typeRef);
    this.type = type as ObjectType;
  }

  next(): PropertyValue | undefined {
    if (this.walkers.length == 0) {
      return undefined;
    }
    const walker = _peek(this.walkers);
    let propertyValue: PropertyValue = walker.next();
    if (propertyValue) {
      if (propertyValue.value) {
        let type = this.schema.getType(propertyValue.property.type);
        //@todo looks liked  propertyValue.value can't be instanceof SourcedValue  so fromSourcedValue always == propertyValue.value
        //let fromSourcedValue = (propertyValue.property.opts.needSource && propertyValue.value instanceof SourcedValue) ? propertyValue.value.value : propertyValue.value;
        let fromSourcedValue =  propertyValue.value;
        let goFurther = true;
        if (this.stopProcessingCallback){
          goFurther = this.stopProcessingCallback(propertyValue);
        }
        if (goFurther) {
          if (type.kind == TypeKind.Object && fromSourcedValue instanceof Object) {
            this.walkers.push(new OneLevelModelWalker(this.schema, {
              value: getPropertyValue(propertyValue.property.name, propertyValue.parent!.value),
              property: propertyValue.property,
              parent: propertyValue.parent
            }, this.stopProcessingCallback));
          } else if (type.kind == TypeKind.List) {
            this.walkers.push(new OneLevelListWalker(this.schema, {
              value: propertyValue.value,
              property: propertyValue.property,
              parent: propertyValue.parent
            }, this.stopProcessingCallback));
            // throw new Error('List support not implemented')
          }
        }
        return propertyValue;
      } else {
        return propertyValue;
      }
    } else {
      this.walkers.pop();
      return this.next();
    }
  }
}

function _peek(arr: Array<any>): any {
  return arr[arr.length - 1];
}
